import { isServer } from '@mr-yum/frontend-core/dist/support/env'
import { PaymentProcessor } from '@mr-yum/payment-proto/node/payment/v1/payment_processor'
import { useStripe } from '@stripe/react-stripe-js'
import { PaymentRequest } from '@stripe/stripe-js'
import React, { useContext } from 'react'
import { Any } from 'ts-toolbelt'
import { CombinedError, useQuery } from 'urql'
import z from 'zod'

import { useEonxEnvContext } from '@/components/EonX/EonxEnv'
import ApplePay from '@/components/Icons/ApplePay'
import CashIcon from '@/components/Icons/Cash'
import CreditCard from '@/components/Icons/CreditCard'
import GooglePay from '@/components/Icons/GooglePay'
import Tab from '@/components/Icons/Tab'
import UnpaidIcon from '@/components/Icons/Unpaid'
import {
  useOrderingTypeContext,
  useVenueContext,
} from '@/contexts/VenueOrderContext'
import {
  ListPaymentMethodsDocument,
  ListPaymentMethodsQuery,
  NewPaymentFormQuery,
  OrderingType,
  PaymentMethod as ApiPaymentMethod,
  PaymentProcessorType as OldPaymentProcessorType,
  PaymentV2MetaQuery,
  SupportedPaymentProcessor,
} from '@/gql/graphql'
import {
  PaymentRequestDevice,
  useInitStripePaymentRequest,
} from '@/hooks/useInitPaymentRequest'
import { BookingDetails, CustomerStoreContext } from '@/stores/CustomerStore'

import { paymentMethodSchema } from '../PaymentSchema'
import { getCardLogoForBrand } from '../Stripe/utils'

export const POST_PAID_PROCESSOR_TYPES: PaymentProcessor[] = [
  PaymentProcessor.CASH,
  PaymentProcessor.POST_PAID,
]

export type PaymentMethodValue = z.infer<
  typeof paymentMethodSchema
>['paymentMethod']

export type PaymentMethod = PaymentMethodValue & {
  position: number
  Icon: React.ElementType
  label: string | React.ReactNode
  tracking: string
}

export enum QuickPaymentMethodKey {
  applePay = 'Apple Pay',
  googlePay = 'Google Pay',
}
// Apple/Google Pay
export interface StripePaymentRequestMethod extends PaymentMethod {
  key: PaymentMethodKey.StripePaymentRequest
  label: QuickPaymentMethodKey
  tracking: QuickPaymentMethodKey
  value: null
}

export interface StripeExistingCardPaymentMethod extends PaymentMethod {
  value: string
  key: PaymentMethodKey.StripeExistingCard
}

export interface StripeNewCardPaymentMethod extends PaymentMethod {
  value: null
  key: PaymentMethodKey.StripeNewCard
}

export interface PaystackExistingCardPaymentMethod extends PaymentMethod {
  value: string
  key: PaymentMethodKey.PaystackExistingCard
}

export interface ZeroAmountPaymentMethod extends PaymentMethod {
  value: null
  key: PaymentMethodKey.ZeroAmount
}
export interface EonxPaymentMethod extends PaymentMethod {
  value: null
  key: PaymentMethodKey.Eonx
}

export interface SharedBillPaymentMethod extends PaymentMethodValue {
  value: string
  key: PaymentMethodKey.TabExisting
}

// Apple/Google Pay
export const isStripePaymentRequest = (
  paymentMethod: PaymentMethodValue,
): paymentMethod is Pick<
  StripePaymentRequestMethod,
  Any.Keys<PaymentMethodValue>
> => paymentMethod.key === PaymentMethodKey.StripePaymentRequest

export const isSharedBillPaymentMethod = (
  paymentMethod: PaymentMethodValue,
): paymentMethod is Pick<
  SharedBillPaymentMethod,
  Any.Keys<PaymentMethodValue>
> => !!paymentMethod.value && paymentMethod.key === PaymentMethodKey.TabExisting

export const isStripeExistingCardMethod = (
  paymentMethod: PaymentMethodValue,
): paymentMethod is Pick<
  StripeExistingCardPaymentMethod,
  Any.Keys<PaymentMethodValue>
> =>
  !!paymentMethod.value &&
  paymentMethod.key === PaymentMethodKey.StripeExistingCard

export const isStripeNewCardMethod = (
  paymentMethod: PaymentMethodValue,
): paymentMethod is Pick<
  StripeNewCardPaymentMethod,
  Any.Keys<PaymentMethodValue>
> => paymentMethod.key === PaymentMethodKey.StripeNewCard

/**
 * If we made the zod type a union type, we wouldn't need all of these type guards
 * to ensure that value is set where appropriate, and we wouldn't need the Zod superRefine
 */
export const isPaystackExistingCardMethod = (
  paymentMethod: PaymentMethodValue,
): paymentMethod is Pick<
  PaystackExistingCardPaymentMethod,
  Any.Keys<PaymentMethodValue>
> =>
  !!paymentMethod.value &&
  paymentMethod.key === PaymentMethodKey.PaystackExistingCard

interface UsePaymentMethodArgs {
  totalInCents: number
  venue: NonNullable<NewPaymentFormQuery['guestVenue']>
  paymentProcessors: NonNullable<PaymentV2MetaQuery['listPaymentProcessors']>
  isNewGroupOrder?: boolean
}

export interface UsePaymentMethodsReturnType {
  paymentRequest?: PaymentRequest | null
  paymentRequestFetching: boolean
  paymentMethods: PaymentMethod[]
  /**
   * Is any paymentMethod still fetching ?
   */
  paymentMethodsFetching: boolean
  paymentMethodsErrors: CombinedError[]
  hasStripe: boolean
}

export enum PaymentMethodKey {
  TabNew = 'tab-new',
  TabExisting = 'tab-existing',
  StripeExistingCard = 'stripe-existing',
  StripeNewCard = 'stripe-new-card',
  // Apple/Google Pay
  StripePaymentRequest = 'stripe-payment-request',
  ChargeToRoom = 'charge-to-room',
  Cash = 'cash',
  Unpaid = 'unpaid',
  Postpaid = 'post-paid',
  // Currently distinct from Unpaid, as Unpaid is currently overloaded to mean "bill added to a tab" at private members club.
  ZeroAmount = 'zero-amount',
  Eonx = 'eonx',
  PaystackNewCard = 'paystack-new-card',
  PaystackExistingCard = 'paystack-existing-card',
}

export interface PaymentMethodOptions extends UsePaymentMethodArgs {
  orderingType: OrderingType
  listPaymentMethods: ListPaymentMethodsQuery['listPaymentMethods']
  listOpenGroupOrderPaymentMethods?: ListPaymentMethodsQuery['listOpenGroupOrderPaymentMethods']['items']
  paymentProcessors: NonNullable<PaymentV2MetaQuery['listPaymentProcessors']>
  paymentRequest?: PaymentRequest | null
  paymentRequestDevice?: PaymentRequestDevice | null
  bookingDetails?: BookingDetails
  unpaidLabel?: string | null
  totalInCents: number
  hasStripe: boolean
  isPaystack: boolean
}

// update this array when the FE implements new payment processors
const SUPPORTED_PAYMENT_PROCESSORS: PaymentProcessor[] = [
  PaymentProcessor.STRIPE,
  PaymentProcessor.CASH,
  PaymentProcessor.POST_PAID,
  PaymentProcessor.ZERO_AMOUNT,
  PaymentProcessor.PAYSTACK,
]

// The payment methods that can be used to pay for a tab
export const TAB_PAYMENT_PROCESSOR_PAYMENT_METHOD_PROVIDERS: PaymentMethodKey[] =
  [
    PaymentMethodKey.StripeExistingCard,
    PaymentMethodKey.StripeNewCard,
    PaymentMethodKey.StripePaymentRequest,
  ]

export const zeroAmountPaymentMethod: ZeroAmountPaymentMethod = {
  key: PaymentMethodKey.ZeroAmount,
  value: null,
  processorType: PaymentProcessor.ZERO_AMOUNT,
  position: 1,
  label: '',
  tracking: 'Zero Amount Order',
  Icon: UnpaidIcon,
}
export const eonxPaymentMethod = ({
  brand,
  last4,
  expiryMonth,
  expiryYear,
}: ApiPaymentMethod): EonxPaymentMethod => {
  return {
    key: PaymentMethodKey.Eonx,
    value: null,
    processorType: PaymentProcessor.EONX,
    position: 1,
    label: (
      <>
        {brand.toUpperCase()} &bull;&bull;&bull;&bull; {last4} {expiryMonth}/
        {expiryYear}
      </>
    ),
    tracking: 'EONX',
    Icon: () => getCardLogoForBrand(brand),
  }
}

export const paymentProcessorsIncludeStripe = (
  processorTypes: SupportedPaymentProcessor[],
) => {
  return processorTypes.some(({ name }) => name === PaymentProcessor.STRIPE)
}

const paymentProcessorsIncludePaystack = (
  processorTypes: SupportedPaymentProcessor[],
) => {
  return processorTypes.some(({ name }) => name === PaymentProcessor.PAYSTACK)
}

const paymentProcessorPaymentMethodsQuery = ({
  isEonxEnv,
  isPaystack,
}: {
  isEonxEnv: boolean | null
  isPaystack: boolean
}) => {
  if (isEonxEnv) return PaymentProcessor.EONX
  if (isPaystack) return PaymentProcessor.PAYSTACK
  return PaymentProcessor.STRIPE
}

const shouldSkipPaymentMethodsQuery = ({
  isEonxEnv,
  isPaystack,
  isServer,
  hasStripe,
}: {
  isEonxEnv: boolean | null
  isPaystack: boolean
  isServer: boolean
  hasStripe: boolean
}): boolean => {
  if (isServer) return true
  if (isEonxEnv) return false
  if (!isPaystack && !hasStripe) return true
  return false
}

export const getPaymentMethods = ({
  listPaymentMethods,
  listOpenGroupOrderPaymentMethods,
  paymentProcessors,
  unpaidLabel,
  paymentRequest,
  paymentRequestDevice,
  totalInCents,
  hasStripe,
  isNewGroupOrder,
  isPaystack,
}: PaymentMethodOptions): PaymentMethod[] => {
  const hasOwnedOpenGroupOrderPaymentMethods =
    !!listOpenGroupOrderPaymentMethods?.some(
      (groupOrderPaymentMethod) =>
        groupOrderPaymentMethod.ownershipType === 'OWNER',
    )

  if (
    paymentProcessors.every((p) => p.name === PaymentProcessor.EONX) &&
    listPaymentMethods?.length
  ) {
    return [eonxPaymentMethod(listPaymentMethods[0])]
  }

  // If the total is $0, there is no need to display payment methods.. right?
  // Note: This may change with external gateways like EONX, where a guest might
  // be using The Pass app and be discounted to $0?
  // And is not new tab...
  if (!isNewGroupOrder && totalInCents === 0) {
    return [zeroAmountPaymentMethod]
  }

  /*
    Split Payment Processors from UI Payment Method radio buttons
  */
  const paymentMethods: PaymentMethod[] = []
  const stripeNewCardPaymentMethod: StripeNewCardPaymentMethod = {
    key: PaymentMethodKey.StripeNewCard,
    value: null,
    processorType: PaymentProcessor.STRIPE,
    position: 4,
    label: 'New credit/debit card',
    tracking: 'New Stripe card',
    Icon: CreditCard,
  }

  if (hasStripe) {
    paymentMethods.push(stripeNewCardPaymentMethod)
  }

  if (isPaystack) {
    paymentMethods.push({
      key: PaymentMethodKey.PaystackNewCard,
      value: null,
      processorType: PaymentProcessor.PAYSTACK,
      position: 4,
      label: 'Apple Pay / Google Pay / Credit Card',
      tracking: 'New Paystack card',
      Icon: CreditCard,
    })
  }

  if (paymentRequest && paymentRequestDevice) {
    const paymentRequestPaymentMethod: StripePaymentRequestMethod = {
      key: PaymentMethodKey.StripePaymentRequest,
      value: null,
      processorType: PaymentProcessor.STRIPE,
      position: 2,
      label: paymentRequestDevice.applePay
        ? QuickPaymentMethodKey.applePay
        : QuickPaymentMethodKey.googlePay,
      tracking: paymentRequestDevice.applePay
        ? QuickPaymentMethodKey.applePay
        : QuickPaymentMethodKey.googlePay,
      Icon: paymentRequestDevice.applePay ? ApplePay : GooglePay,
    }
    paymentMethods.push(paymentRequestPaymentMethod)
  }

  const paymentProcessorMethods = paymentProcessors
    .map<PaymentMethod | null>(({ name }) => {
      if (name === PaymentProcessor.CASH) {
        return {
          key: PaymentMethodKey.Cash,
          position: 100,
          processorType: PaymentProcessor.CASH,
          label: 'Cash',
          tracking: 'Cash',
          Icon: CashIcon,
          value: null,
        }
      } else if (name === OldPaymentProcessorType.Unpaid) {
        // UNPAID is mapped to POST_PAID in V2 flow
        return {
          key: PaymentMethodKey.Postpaid,
          position: 98,
          processorType: PaymentProcessor.POST_PAID,
          label: unpaidLabel ?? 'Pay later',
          tracking: 'Pay later',
          Icon: UnpaidIcon,
          value: null,
        }
      } else if (name === OldPaymentProcessorType.Wallet) {
        // Only display the option to start a new tab if there are no existing tabs
        // owned by the current user.
        return !hasOwnedOpenGroupOrderPaymentMethods
          ? {
              key: PaymentMethodKey.TabNew,
              value: null,
              // Wallet is mapped to STRIPE in the new payment flow.
              processorType: PaymentProcessor.STRIPE,
              position: 99, // Always last before cash
              label: 'Start a new group order',
              tracking: 'New tab',
              Icon: Tab,
            }
          : null
      } else {
        return null
      }
    })
    .filter((paymentMethod): paymentMethod is PaymentMethod => !!paymentMethod)
    .filter(({ processorType }) =>
      SUPPORTED_PAYMENT_PROCESSORS.includes(processorType),
    )
    .filter(({ key }) => {
      if (isNewGroupOrder) {
        return TAB_PAYMENT_PROCESSOR_PAYMENT_METHOD_PROVIDERS.includes(key)
      } else {
        return true
      }
    })

  const stripePaymentMethods = hasStripe
    ? listPaymentMethods?.map<PaymentMethod>(
        ({ id, brand, last4, expiryMonth, expiryYear }) => ({
          key: PaymentMethodKey.StripeExistingCard,
          value: id,
          processorType: PaymentProcessor.STRIPE,
          position: 3,
          label: (
            <>
              {brand.toUpperCase()} &bull;&bull;&bull;&bull; {last4}{' '}
              {expiryMonth}/{expiryYear}
            </>
          ),
          tracking: 'Existing Stripe card',
          Icon: () => getCardLogoForBrand(brand),
        }),
      ) ?? []
    : []

  const paystackPaymentMethods = isPaystack
    ? listPaymentMethods?.map<PaymentMethod>(
        ({ id, brand, last4, expiryMonth, expiryYear }) => ({
          key: PaymentMethodKey.PaystackExistingCard,
          value: id,
          processorType: PaymentProcessor.PAYSTACK,
          position: 3,
          label: (
            <>
              {brand.toUpperCase()} &bull;&bull;&bull;&bull; {last4}{' '}
              {expiryMonth}/{expiryYear}
            </>
          ),
          tracking: 'Existing Paystack card',
          Icon: () => getCardLogoForBrand(brand),
        }),
      ) ?? []
    : []

  const tabPaymentMethods =
    listOpenGroupOrderPaymentMethods?.map<PaymentMethod>((pm) => ({
      key: PaymentMethodKey.TabExisting,
      // hacky way to store payment targetId and paymentMethodId in one string
      // maybe make a different value type in zod before launching this properly
      value: `${pm.paymentTargetId}:${pm.id}`,
      processorType: PaymentProcessor.STRIPE,
      position: 1,
      label: pm.name,
      tracking: 'Existing tab',
      Icon: Tab,
    })) ?? []

  return (
    stripePaymentMethods
      .concat(paymentMethods)
      .concat(paymentProcessorMethods)
      .concat(paystackPaymentMethods)
      // Can't create a new tab with an existing tab..
      .concat(isNewGroupOrder ? [] : tabPaymentMethods)
      .flat()
      .sort((a, b) => a.position - b.position)
  )
}

/**
 * Return available payment methods
 */
export const usePaymentFormMethods = ({
  totalInCents,
  venue,
  paymentProcessors,
  isNewGroupOrder,
}: UsePaymentMethodArgs): UsePaymentMethodsReturnType => {
  const currency = venue.currency
  const { venueSlug } = useVenueContext()
  const { orderingType } = useOrderingTypeContext()

  const { getBookingDetailsForVenueSlug } = useContext(CustomerStoreContext)
  const bookingDetails = getBookingDetailsForVenueSlug(venueSlug)
  const unpaidLabel = venue.paymentProcessor?.unpaidProcessorTypeLabel

  const stripe = useStripe()

  const hasStripe =
    !!stripe && paymentProcessorsIncludeStripe(paymentProcessors)
  const { isEonxEnv } = useEonxEnvContext()
  const isPaystack = paymentProcessorsIncludePaystack(paymentProcessors)

  const [{ data, fetching, error }] = useQuery({
    query: ListPaymentMethodsDocument,
    variables: {
      currency,
      paymentProcessor: paymentProcessorPaymentMethodsQuery({
        isEonxEnv,
        isPaystack,
      }),
    },
    pause: shouldSkipPaymentMethodsQuery({
      isEonxEnv,
      isPaystack,
      isServer,
      hasStripe,
    }),
    requestPolicy: 'network-only',
  })

  let noEonxPaymentMethodError

  if (!fetching && !error && isEonxEnv && !data?.listPaymentMethods?.length) {
    noEonxPaymentMethodError = new CombinedError({
      graphQLErrors: [
        new Error(
          'Please add payment methods within the Pass app before proceeding with the order submission.',
        ),
      ],
    })
  }

  /**
   * Apple / Google Pay
   * Won't load if Stripe is not enabled
   */
  const { paymentRequestCheckResult, paymentRequestFetching } =
    useInitStripePaymentRequest({ totalInCents, currency, hasStripe })
  const paymentRequest = paymentRequestCheckResult?.paymentRequest
  const paymentRequestDevice = paymentRequestCheckResult?.device

  const paymentMethods = getPaymentMethods({
    orderingType,
    paymentProcessors,
    // we only want to show the latest
    listPaymentMethods: data?.listPaymentMethods?.slice(0, 1),
    // can't use a tab payment method to start a new tab.
    listOpenGroupOrderPaymentMethods: isNewGroupOrder
      ? []
      : data?.listOpenGroupOrderPaymentMethods.items,
    paymentRequest,
    paymentRequestDevice,
    bookingDetails,
    unpaidLabel,
    totalInCents,
    hasStripe,
    isNewGroupOrder,
    venue,
    isPaystack,
  })

  const paymentMethodsFetching = fetching || paymentRequestFetching
  const paymentMethodsErrors = error
    ? [error]
    : noEonxPaymentMethodError
      ? [noEonxPaymentMethodError]
      : []

  return {
    paymentRequest,
    paymentRequestFetching,
    paymentMethods,
    paymentMethodsFetching,
    paymentMethodsErrors,
    hasStripe,
  }
}
